/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import AwsS3 from '@uppy/aws-s3';
import Uppy from '@uppy/core';
import { Dashboard, DashboardModal } from '@uppy/react';

import initBem from 'js/lib/bem';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, InlineNotification, LoadingItem, typography2 } from '@coursera/cds-core';
import { UploadIcon } from '@coursera/cds-icons';

import FileUploadErrorModal from 'bundles/fileuploader-uppy/components/FileUploadErrorModal';
import PreSignedUrlProvider from 'bundles/fileuploader-uppy/components/PreSignedUrlProvider';
import type {
  GetPreSignedUrlInput,
  GetPreSignedUrlResult,
} from 'bundles/fileuploader-uppy/components/PreSignedUrlProvider';
import UPLOAD_CONFIG from 'bundles/fileuploader-uppy/constants/FileUploadConfig';
import type {
  FileUploaderUppy,
  TBody,
  TMeta,
  UppyEventMap,
  UppyFile,
} from 'bundles/fileuploader-uppy/types/UppyFileUploader';
import { buildS3UploadParams, calcCheckSum } from 'bundles/fileuploader-uppy/utils/FileUploadUtils';

import _t from 'i18n!nls/fileuploader-uppy';

import 'css!@uppy/core/dist/style.min.css';
import 'css!@uppy/dashboard/dist/style.min.css';

const styles = {
  root: css`
    /* OVERRIDE BROKEN UPPY STYLES */
    .uppy-DashboardContent-bar {
      position: static;
    }

    .uppy-DashboardContent-back {
      position: static;
    }

    .uppy-Dashboard-files {
      position: static;
    }

    .uppy-Dashboard-progressindicators {
      position: static;
    }

    .uppy-Dashboard-browse {
      color: var(--cds-color-blue-700);
      text-decoration: underline;

      &:focus,
      &:hover {
        color: var(--cds-color-blue-800);
        border-color: var(--cds-color-blue-800);
      }
    }

    .uppy-Dashboard-close:focus,
    .uppy-Dashboard-close:hover {
      color: var(--cds-color-blue-950);
    }

    .uppy-Dashboard-close:focus {
      outline: 2px solid var(--cds-color-purple-900);
      outline-offset: 3px;
    }

    @media (forced-colors: active) {
      .cds-Dialog-dialog {
        border: 1px ButtonText solid;
      }
    }
  `,
  customModalRoot: css`
    .uppy-Dashboard-inner {
      background-color: transparent;
      border: none;
      border-radius: 0;
    }

    .uppy-Dashboard-AddFiles {
      margin: 0;
    }

    .uppy-Dashboard-AddFiles-title {
      color: var(--cds-color-neutral-primary);
      ${typography2.subtitleMedium}
      margin: var(--cds-spacing-100) 0;
    }

    .uppy-Dashboard-Item-name {
      color: var(--cds-color-neutral-primary);
      ${typography2.bodyPrimary}
      margin: var(--cds-spacing-100) 0;
    }

    .uppy-Dashboard-browse {
      border-radius: var(--cds-border-radius-50);
      text-transform: none;
      text-align: center;
      max-width: 320px;
      min-width: unset;
      transition: none 0s ease 0s;
      box-sizing: border-box;
      text-decoration: none;
      color: var(--cds-color-interactive-primary);
      ${typography2.subtitleMedium}
      padding: var(--cds-spacing-100);
      border: 0;
      position: relative;

      &:focus,
      &:hover {
        border: 0;
        text-decoration: underline;
        color: var(--cds-color-interactive-primary-hover);
        background-color: var(--cds-color-interactive-background-primary-hover-weak);
      }

      &:focus-visible {
        outline: transparent solid 1px;

        &::after {
          content: '';
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          border-radius: var(--cds-border-radius-50);
          box-shadow: 0 0 0 1px var(--cds-color-interactive-stroke-primary-focus),
            0 0 0 2px var(--cds-color-interactive-stroke-primary-focus-invert);
        }
      }
    }

    .uppy-StatusBar-actionCircleBtn:focus {
      box-shadow: 0 0 0 3px var(--cds-color-interactive-primary-selected);
    }

    .uppy-DashboardContent-bar {
      display: none;
    }

    .uppy-Dashboard-AddFiles-list {
      display: none;
    }
  `,
  dialogHeading: css`
    padding-bottom: 0;
  `,
  dialogContent: css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-600);
  `,
  dialogActions: (hasUploadStarted: boolean) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-top: 0;
    margin-top: ${hasUploadStarted ? 'calc(var(--cds-spacing-600) * -1)' : '0'};
  `,
  dialogInlineNotification: css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

export type UppyProps = {
  id: string;
  buttonRef?: (ref: HTMLButtonElement) => void;
  buttonContent?: React.ReactNode;
  buttonClasses?: Array<string>;
  buttonHtmlAttributes?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  disabled?: boolean;
  maxNumberOfFiles?: number;
  maxFileSize?: number;
  allowedFileTypes?: Array<string>;
  removeFileButtonRef?: React.RefObject<HTMLButtonElement>;
  onClickAddFile?: () => void;

  // note: Uppy events (see https://uppy.io/docs/uppy/#Events)
  onFileAdded?: (file: UppyFile) => void;
  onFileRemoved?: (file: UppyFile) => void;
  onUpload?: UppyEventMap['upload'];
  onUploadProgress?: UppyEventMap['upload-progress'];
  onUploadSuccess?: UppyEventMap['upload-success'];
  onUploadError?: UppyEventMap['upload-error'];
  onUploadRetry?: UppyEventMap['upload-retry'];
  onComplete?: UppyEventMap['complete'];
  onError?: UppyEventMap['error'];
  onCancelAll?: UppyEventMap['cancel-all'];
  onRestrictionFailed?: UppyEventMap['restriction-failed'];

  // Allow clients to create custom restriction handlers that will run once file is added
  customRestrictionHandler?: (file: UppyFile, uppy: FileUploaderUppy, handleRestrictionFailed: () => void) => void;

  // this allows us to use our custom CDS modal instead of Uppy's dashboard modal
  useCdsModal?: boolean;
};

export type GraphQLProps = {
  getPreSignedUrl: (input: GetPreSignedUrlInput) => Promise<GetPreSignedUrlResult>;
};

type State = {
  isUploadModalOpen: boolean;
  isErrorModalOpen: boolean;
  showRestrictionFailedNotification: boolean;

  // State needed when useCdsModal is true
  isProcessingFiles: boolean;
  isUploadingFiles: boolean;
  numberOfFilesToUpload: number;
  uploadProgress: number;
};

/**
 * This is a hack to fix https://coursera.atlassian.net/browse/ACCESS-833
 *
 * If Uppy modal appears inside opened React Modal, both of modals try to handle the tab focus. It does not
 * cause troubles in all browsers except for Safari.
 *
 * React Modal has workaround that fix tabbing in modal for Safari and it breaks tabbing in Uppy:
 * https://github.com/reactjs/react-modal/blob/master/src/helpers/scopeTab.js#L39-L79
 *
 * I could not find a better solution than to override userAgent to break React Modal Safari detection when Uppy
 * modal is opened, and return original one when it's closed or when this component is unmounted.
 *
 * This is unsafe and fragile solution, it might break on react-modal update, but I could not come up with a
 * better solution for this. The only way I see is to avoid using Uppy modal inside react-modal. For example
 * we can use inline Uppy interface.
 */
const rewriteUserAgent = (from: string, to: string) => {
  if (navigator.userAgent.match(from)) {
    try {
      const newUserAgent = navigator.userAgent.replace(from, to);
      Object.defineProperty(navigator, 'userAgent', { get: () => newUserAgent, configurable: true });
    } catch {
      /* empty */
    }
  }
};

const safariFocusWorkaround = {
  apply: () => rewriteUserAgent('Safari', 'Saf-ari'),
  clear: () => rewriteUserAgent('Saf-ari', 'Safari'),
};

const bem = initBem('UppyFileUploader');
export class UppyFileUploader extends React.Component<UppyProps & GraphQLProps, State> {
  declare uppy: FileUploaderUppy;

  uploadButtonRef = React.createRef<HTMLButtonElement>();

  cancelUploadButtonRef = React.createRef<HTMLButtonElement>();

  constructor(props: UppyProps & GraphQLProps) {
    super(props);

    this.state = {
      isUploadModalOpen: false,
      isErrorModalOpen: false,
      showRestrictionFailedNotification: false,
      // State needed when useCdsModal is true
      isProcessingFiles: false,
      isUploadingFiles: false,
      numberOfFilesToUpload: 0,
      uploadProgress: 0,
    };

    const {
      id,
      maxNumberOfFiles,
      maxFileSize,
      allowedFileTypes,
      onFileAdded,
      onFileRemoved,
      onUpload,
      onUploadProgress,
      onUploadSuccess,
      onUploadError,
      onUploadRetry,
      onComplete,
      onError,
      onCancelAll,
      onRestrictionFailed,
      customRestrictionHandler,
      useCdsModal,
    } = props;

    // eslint-disable-next-line new-cap
    this.uppy = new Uppy<TMeta, TBody>({
      // unique id to avoid collisions if more than one uploader on page
      // (see https://uppy.io/docs/uppy/#id-39-uppy-39)
      id: id + '-Uppy',
      // do not automatically start upload if maxNumberOfFiles > 1
      autoProceed: !(!!maxNumberOfFiles && maxNumberOfFiles > 1),
      allowMultipleUploadBatches: true,
      restrictions: {
        maxFileSize: maxFileSize || UPLOAD_CONFIG.maxFileSize,
        maxNumberOfFiles: maxNumberOfFiles || UPLOAD_CONFIG.maxNumberOfFiles,
        allowedFileTypes: allowedFileTypes || UPLOAD_CONFIG.allowedFileTypes,
      },
      // note: global meta data can be attached to uploads (see https://uppy.io/docs/uppy/#meta)
      meta: {},
    });

    this.uppy.use(AwsS3<TMeta, TBody>, {
      id: id + '-AwsS3',
      shouldUseMultipart: false,
      getUploadParameters: this.getUploadParams,
      allowedMetaFields: true,
    });

    // note: Uppy events (see https://uppy.io/docs/uppy/#Events)
    if (onFileAdded) {
      this.uppy.on('file-added', onFileAdded);
    }
    // if there is a custom restriction handler, run it when a file is added
    if (customRestrictionHandler) {
      this.uppy.on('file-added', (file: UppyFile) =>
        customRestrictionHandler(file, this.uppy, this.handleRestrictionFailed)
      );
    }
    if (onFileRemoved) {
      this.uppy.on('file-removed', onFileRemoved);
    }
    if (onUpload) {
      this.uppy.on('upload', onUpload);
    }
    if (onUploadProgress) {
      this.uppy.on('upload-progress', onUploadProgress);
    }
    if (onUploadSuccess) {
      this.uppy.on('upload-success', onUploadSuccess);
    }
    if (onUploadError) {
      this.uppy.on('upload-error', onUploadError);
    }
    if (onUploadRetry) {
      this.uppy.on('upload-retry', onUploadRetry);
    }
    if (onComplete) {
      this.uppy.on('complete', onComplete);
    }
    if (onError) {
      this.uppy.on('error', onError);
    }
    if (onCancelAll) {
      this.uppy.on('cancel-all', onCancelAll);
    }
    if (onRestrictionFailed) {
      this.uppy.on('restriction-failed', onRestrictionFailed);
    }
    this.uppy.on('upload', this.setUploadSessionIdMeta);
    this.uppy.on('complete', this.clearUploadSessionIdMeta);
    // note: checkForError will reset state and show an error modal, so only reset
    //       state on complete after other event handlers have a chance to view the state
    this.uppy.on('complete', this.checkForError);

    this.uppy.on('complete', safariFocusWorkaround.clear);

    if (useCdsModal) {
      this.uppy.on('upload', this.handleUploadStart);
      this.uppy.on('preprocess-progress', this.handlePreprocessProgress);
      this.uppy.on('progress', this.handleUploadProgress);
      this.uppy.on('complete', this.resetUploadStates);
      this.uppy.on('error', this.resetUploadStates);
      this.uppy.on('cancel-all', this.resetUploadStates);
      this.uppy.on('restriction-failed', this.handleRestrictionFailed);
    }
  }

  componentWillUnmount() {
    this.uppy.destroy();
    safariFocusWorkaround.clear();
  }

  getUploadParams = async (file: UppyFile) => {
    const { getPreSignedUrl } = this.props;
    const checkSum = await calcCheckSum({ file: file.data });
    const preSignedUrlResponse = await getPreSignedUrl({
      contentMd5: checkSum,
      contentType: file.type,
      fileName: file.name ?? '',
    });
    return buildS3UploadParams(preSignedUrlResponse);
  };

  checkForError: UppyEventMap['complete'] = (result) => {
    this.uppy.cancelAll();
    if (result.failed?.length) {
      this.hideUploadModal();
      this.showErrorModal();
    }
  };

  // TODO: Verify this works as expected (upload event signature discrepancy)
  setUploadSessionIdMeta: UppyEventMap['upload'] = (id) => {
    this.uppy.setMeta({ uploadSessionId: id });
  };

  clearUploadSessionIdMeta = () => {
    this.uppy.setMeta({ uploadSessionId: null });
  };

  handleUploadStart: UppyEventMap['upload'] = (_, files) => {
    const fileIDs = files.map((file) => file.id);
    this.setState({
      isUploadingFiles: true,
      numberOfFilesToUpload: fileIDs.length,
      showRestrictionFailedNotification: false,
    });
  };

  handlePreprocessProgress = () => {
    this.cancelUploadButtonRef?.current?.focus();
    this.setState({ isProcessingFiles: true });
  };

  handleUploadProgress = (progress: number) => {
    this.setState({ isProcessingFiles: false, uploadProgress: progress });
  };

  resetUploadStates = () => {
    this.setState({
      isUploadingFiles: false,
      isProcessingFiles: false,
      numberOfFilesToUpload: 0,
      uploadProgress: 0,
      showRestrictionFailedNotification: false,
    });
  };

  handleRestrictionFailed = () => {
    this.setState({ showRestrictionFailedNotification: true });
  };

  showUploadModal = () => {
    this.props.onClickAddFile?.();
    this.setState({ isUploadModalOpen: true, showRestrictionFailedNotification: false });
    safariFocusWorkaround.apply();
  };

  hideUploadModal = () => {
    this.setState({ isUploadModalOpen: false });
    safariFocusWorkaround.clear();
  };

  showErrorModal = () => this.setState({ isErrorModalOpen: true });

  hideErrorModal = () => this.setState({ isErrorModalOpen: false });

  getDialogHeading = ({
    isProcessingFiles,
    isUploadingFiles,
    maxNumberOfFiles,
  }: {
    isProcessingFiles: boolean;
    isUploadingFiles: boolean;
    maxNumberOfFiles: number;
  }) => {
    if (isProcessingFiles) {
      return _t('Processing {numberOfFilesToUpload, plural, =1 {# file} other {# files}}...');
    }

    if (isUploadingFiles) {
      return _t('Uploading {numberOfFilesToUpload, plural, =1 {# file} other {# files}}...');
    }

    return maxNumberOfFiles === 1 ? _t('Upload File') : _t('Upload Files');
  };

  renderCustomModal = (closeModalReturnRef: React.RefObject<HTMLButtonElement>) => {
    const { maxNumberOfFiles } = this.props;
    const {
      isUploadModalOpen,
      isProcessingFiles,
      isUploadingFiles,
      numberOfFilesToUpload,
      uploadProgress,
      showRestrictionFailedNotification,
    } = this.state;
    const hasUploadStarted = isProcessingFiles || isUploadingFiles;

    return (
      <Dialog
        id="custom-uppy-modal"
        variant={hasUploadStarted ? 'transactional' : 'standard'}
        open={isUploadModalOpen}
        onClose={this.hideUploadModal}
        aria-labelledby="dialog-heading"
        aria-describedby="dialog-content"
        renderInPlace={true}
        css={[styles.root, styles.customModalRoot]}
        {...(closeModalReturnRef?.current && {
          returnFocusRef: closeModalReturnRef,
        })}
      >
        <Dialog.HeadingGroup id="dialog-heading" css={styles.dialogHeading}>
          {
            // Dialog.HeadingGroup expects a string, but FormattedMessage returns a span
            (
              <FormattedMessage
                message={this.getDialogHeading({
                  isProcessingFiles,
                  isUploadingFiles,
                  maxNumberOfFiles: maxNumberOfFiles || UPLOAD_CONFIG.maxNumberOfFiles,
                })}
                numberOfFilesToUpload={numberOfFilesToUpload}
              />
            ) as unknown as string
          }
        </Dialog.HeadingGroup>

        <Dialog.Content id="dialog-content" css={styles.dialogContent}>
          {showRestrictionFailedNotification && (
            <InlineNotification severity="error" css={styles.dialogInlineNotification}>
              {_t('This file cannot be uploaded. Please check the file type, size, or quantity limits.')}
            </InlineNotification>
          )}
          <Dashboard
            uppy={this.uppy}
            disableStatusBar={true}
            disableInformer={true}
            proudlyDisplayPoweredByUppy={false}
            height={150}
          />
        </Dialog.Content>

        {hasUploadStarted && (
          <Dialog.Actions css={styles.dialogActions(hasUploadStarted)}>
            <LoadingItem
              role="alert"
              aria-live="assertive"
              label={isProcessingFiles ? _t('Preparing upload...') : _t('Uploading...')}
              {...(!isProcessingFiles && {
                value: uploadProgress,
              })}
            />
            <Button
              aria-label={_t('Cancel upload')}
              size="medium"
              variant="secondary"
              onClick={this.uppy.cancelAll}
              ref={this.cancelUploadButtonRef}
            >
              {_t('Cancel')}
            </Button>
          </Dialog.Actions>
        )}
      </Dialog>
    );
  };

  render() {
    const {
      buttonClasses,
      buttonContent,
      disabled,
      buttonHtmlAttributes,
      buttonRef,
      useCdsModal,
      removeFileButtonRef,
    } = this.props;
    const { isUploadModalOpen, isErrorModalOpen } = this.state;
    // focus the remove file button or the upload file button when the modal closes
    const closeModalReturnRef = removeFileButtonRef?.current ? removeFileButtonRef : this.uploadButtonRef;

    return (
      <div className={bem(undefined, undefined, buttonClasses)} css={styles.root}>
        <Button
          type="button"
          variant="secondary"
          iconPosition="before"
          icon={<UploadIcon size="small" />}
          size="small"
          onClick={this.showUploadModal}
          disabled={disabled}
          ref={(ref) => {
            if (ref) {
              buttonRef?.(ref);
            }

            this.uploadButtonRef = {
              current: ref,
            };
          }}
          {...buttonHtmlAttributes}
        >
          {buttonContent || _t('Upload File')}
        </Button>
        {useCdsModal ? (
          this.renderCustomModal(closeModalReturnRef)
        ) : (
          <DashboardModal
            uppy={this.uppy}
            open={isUploadModalOpen}
            onRequestClose={this.hideUploadModal}
            closeModalOnClickOutside={true}
            closeAfterFinish={true}
            proudlyDisplayPoweredByUppy={false}
          />
        )}

        {isErrorModalOpen && (
          <FileUploadErrorModal handleClose={this.hideErrorModal} closeModalReturnRef={closeModalReturnRef} />
        )}
      </div>
    );
  }
}

const UppyFileUploaderWithProvider: React.FC<UppyProps> = (props) => {
  return (
    <PreSignedUrlProvider>
      {(getPreSignedUrl) => <UppyFileUploader {...props} getPreSignedUrl={getPreSignedUrl} />}
    </PreSignedUrlProvider>
  );
};

export default UppyFileUploaderWithProvider;
